/**
 * ProductAEOViewer Component
 *
 * Displays Product AEO content with proper formatting matching the generation preview.
 * Supports both database format (camelCase) and transformed format (snake_case).
 *
 * Sections rendered:
 * - Summary (featured_snippet, full_description, key_differentiators)
 * - Best For / Use Cases
 * - Benefits / Features
 * - FAQ
 * - Competitive Comparison (analysis)
 * - Brand Product Range (brandTiers with Good/Better/Best)
 * - Competitor Alternatives
 * - Alternative Recommendations
 * - Buyer Profiles
 * - Limitations
 * - Specifications
 */

import React, { useEffect, useId, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Tag,
  Package,
  ExternalLink,
  CheckCircle2,
  CheckCircle,
  FileText,
  Target,
  HelpCircle,
  TrendingUp,
  Info,
  Users,
  Zap,
  Star,
  List,
  AlertTriangle,
  Sparkles,
  Copy,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cleanProductUrl } from '@/utils/url-cleaner';
// U-28: section-key → placeholder label from the shared canonical mirror (kept in sync with
// backend-merged/shared/aeoSections.json by tests/services/aeoSectionSymmetry.test.js).
import { SECTION_LABELS } from '@/aeo/sections';
import { humanizeKey } from '@/utils/humanize-key';
import { formatProductAttributeValue } from '@/utils/product-attribute-display';

// Map ISO 639-1 codes to display names. Source-of-truth for "all 17 languages
// translated" enumeration is the site's aeo_target_languages array; this map
// only renders friendly labels for what's there.
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
  pt: 'Portuguese', nl: 'Dutch', pl: 'Polish', ru: 'Russian', ja: 'Japanese',
  zh: 'Chinese', ko: 'Korean', ar: 'Arabic', tr: 'Turkish', sv: 'Swedish',
  da: 'Danish', no: 'Norwegian', fi: 'Finnish', cs: 'Czech', el: 'Greek',
  he: 'Hebrew', hi: 'Hindi', th: 'Thai', vi: 'Vietnamese', id: 'Indonesian',
  uk: 'Ukrainian', ro: 'Romanian', hu: 'Hungarian', ca: 'Catalan',
};

const languageLabel = (code: string): string =>
  LANGUAGE_NAMES[code] || code.toUpperCase();


// User-visible UI labels translated for the supported AEO target languages.
// Keys are stable; values per language. Falls back to `en` for unknown codes
// or when `currentLanguage` prop isn't passed (offering modal, future PDF).
//
// Coverage: the 12 most-prominent labels Clara's TradeInn review surfaced
// staying in English when the rest of the content was translated. Other
// minor strings (badges, table headers) intentionally left for a follow-up
// to keep this PR small + reviewable.
type UILabelKey =
  | 'productSummary' | 'keyFeatures' | 'useCases' | 'bestFor' | 'benefits'
  | 'idealBuyerProfiles' | 'faq' | 'competitiveComparison'
  | 'limitationsToConsider' | 'specifications'
  | 'keyFeaturesInline' | 'note' | 'showAllSpecifications' | 'showFewerSpecifications';

const UI_LABELS: Record<string, Record<UILabelKey, string>> = {
  en: { productSummary: 'Product Summary', keyFeatures: 'Key Features', useCases: 'Use Cases', bestFor: 'Best For', benefits: 'Benefits', idealBuyerProfiles: 'Ideal Buyer Profiles', faq: 'Frequently Asked Questions', competitiveComparison: 'Competitive Comparison', limitationsToConsider: 'Limitations to Consider', specifications: 'Specifications', keyFeaturesInline: 'Key features', note: 'Note', showAllSpecifications: 'Show all specifications', showFewerSpecifications: 'Show fewer specifications' },
  es: { productSummary: 'Resumen del Producto', keyFeatures: 'Características Clave', useCases: 'Casos de Uso', bestFor: 'Ideal Para', benefits: 'Beneficios', idealBuyerProfiles: 'Perfiles de Comprador Ideal', faq: 'Preguntas Frecuentes', competitiveComparison: 'Comparativa Competitiva', limitationsToConsider: 'Limitaciones a Considerar', specifications: 'Especificaciones', keyFeaturesInline: 'Características clave', note: 'Nota', showAllSpecifications: 'Mostrar todas las especificaciones', showFewerSpecifications: 'Mostrar menos especificaciones' },
  ca: { productSummary: 'Resum del Producte', keyFeatures: 'Característiques Clau', useCases: "Casos d'Ús", bestFor: 'Ideal Per A', benefits: 'Beneficis', idealBuyerProfiles: 'Perfils de Comprador Ideal', faq: 'Preguntes Freqüents', competitiveComparison: 'Comparativa Competitiva', limitationsToConsider: 'Limitacions a Considerar', specifications: 'Especificacions', keyFeaturesInline: 'Característiques clau', note: 'Nota', showAllSpecifications: 'Mostra totes les especificacions', showFewerSpecifications: 'Mostra menys especificacions' },
  fr: { productSummary: 'Résumé du Produit', keyFeatures: 'Caractéristiques Principales', useCases: "Cas d'Usage", bestFor: 'Idéal Pour', benefits: 'Avantages', idealBuyerProfiles: 'Profils Acheteurs Idéaux', faq: 'Questions Fréquentes', competitiveComparison: 'Comparaison Concurrentielle', limitationsToConsider: 'Limitations à Considérer', specifications: 'Spécifications', keyFeaturesInline: 'Caractéristiques clés', note: 'Note', showAllSpecifications: 'Afficher toutes les spécifications', showFewerSpecifications: 'Afficher moins de spécifications' },
  de: { productSummary: 'Produktübersicht', keyFeatures: 'Hauptmerkmale', useCases: 'Anwendungsfälle', bestFor: 'Am besten für', benefits: 'Vorteile', idealBuyerProfiles: 'Ideale Käuferprofile', faq: 'Häufig gestellte Fragen', competitiveComparison: 'Wettbewerbsvergleich', limitationsToConsider: 'Zu beachtende Einschränkungen', specifications: 'Spezifikationen', keyFeaturesInline: 'Hauptmerkmale', note: 'Hinweis', showAllSpecifications: 'Alle Spezifikationen anzeigen', showFewerSpecifications: 'Weniger Spezifikationen anzeigen' },
  it: { productSummary: 'Riepilogo del Prodotto', keyFeatures: 'Caratteristiche Principali', useCases: "Casi d'Uso", bestFor: 'Ideale Per', benefits: 'Vantaggi', idealBuyerProfiles: 'Profili Acquirente Ideale', faq: 'Domande Frequenti', competitiveComparison: 'Confronto Competitivo', limitationsToConsider: 'Limitazioni da Considerare', specifications: 'Specifiche', keyFeaturesInline: 'Caratteristiche principali', note: 'Nota', showAllSpecifications: 'Mostra tutte le specifiche', showFewerSpecifications: 'Mostra meno specifiche' },
  pt: { productSummary: 'Resumo do Produto', keyFeatures: 'Principais Características', useCases: 'Casos de Uso', bestFor: 'Ideal Para', benefits: 'Benefícios', idealBuyerProfiles: 'Perfis de Comprador Ideal', faq: 'Perguntas Frequentes', competitiveComparison: 'Comparação Competitiva', limitationsToConsider: 'Limitações a Considerar', specifications: 'Especificações', keyFeaturesInline: 'Características principais', note: 'Nota', showAllSpecifications: 'Mostrar todas as especificações', showFewerSpecifications: 'Mostrar menos especificações' },
  nl: { productSummary: 'Productoverzicht', keyFeatures: 'Belangrijkste Kenmerken', useCases: 'Toepassingen', bestFor: 'Het Beste Voor', benefits: 'Voordelen', idealBuyerProfiles: 'Ideale Koperprofielen', faq: 'Veelgestelde Vragen', competitiveComparison: 'Concurrentievergelijking', limitationsToConsider: 'Te Overwegen Beperkingen', specifications: 'Specificaties', keyFeaturesInline: 'Belangrijkste kenmerken', note: 'Opmerking', showAllSpecifications: 'Alle specificaties tonen', showFewerSpecifications: 'Minder specificaties tonen' },
  sv: { productSummary: 'Produktsammanfattning', keyFeatures: 'Viktiga Funktioner', useCases: 'Användningsfall', bestFor: 'Bäst För', benefits: 'Fördelar', idealBuyerProfiles: 'Ideala Köparprofiler', faq: 'Vanliga Frågor', competitiveComparison: 'Konkurrentjämförelse', limitationsToConsider: 'Begränsningar att Överväga', specifications: 'Specifikationer', keyFeaturesInline: 'Viktiga funktioner', note: 'Obs', showAllSpecifications: 'Visa alla specifikationer', showFewerSpecifications: 'Visa färre specifikationer' },
  da: { productSummary: 'Produktoversigt', keyFeatures: 'Nøglefunktioner', useCases: 'Anvendelsestilfælde', bestFor: 'Bedst Til', benefits: 'Fordele', idealBuyerProfiles: 'Ideelle Køberprofiler', faq: 'Ofte Stillede Spørgsmål', competitiveComparison: 'Konkurrencesammenligning', limitationsToConsider: 'Begrænsninger at Overveje', specifications: 'Specifikationer', keyFeaturesInline: 'Nøglefunktioner', note: 'Bemærk', showAllSpecifications: 'Vis alle specifikationer', showFewerSpecifications: 'Vis færre specifikationer' },
  no: { productSummary: 'Produktoversikt', keyFeatures: 'Nøkkelfunksjoner', useCases: 'Bruksområder', bestFor: 'Best For', benefits: 'Fordeler', idealBuyerProfiles: 'Ideelle Kjøperprofiler', faq: 'Ofte Stilte Spørsmål', competitiveComparison: 'Konkurranselsammenligning', limitationsToConsider: 'Begrensninger å Vurdere', specifications: 'Spesifikasjoner', keyFeaturesInline: 'Nøkkelfunksjoner', note: 'Merk', showAllSpecifications: 'Vis alle spesifikasjoner', showFewerSpecifications: 'Vis færre spesifikasjoner' },
  fi: { productSummary: 'Tuotteen Yhteenveto', keyFeatures: 'Tärkeimmät Ominaisuudet', useCases: 'Käyttötapaukset', bestFor: 'Sopii Parhaiten', benefits: 'Edut', idealBuyerProfiles: 'Ihanteellinen Ostajaprofiili', faq: 'Usein Kysytyt Kysymykset', competitiveComparison: 'Kilpailijavertailu', limitationsToConsider: 'Huomioon Otettavat Rajoitukset', specifications: 'Tekniset Tiedot', keyFeaturesInline: 'Tärkeimmät ominaisuudet', note: 'Huomio', showAllSpecifications: 'Näytä kaikki tekniset tiedot', showFewerSpecifications: 'Näytä vähemmän teknisiä tietoja' },
  pl: { productSummary: 'Podsumowanie Produktu', keyFeatures: 'Kluczowe Cechy', useCases: 'Zastosowania', bestFor: 'Najlepszy Do', benefits: 'Zalety', idealBuyerProfiles: 'Idealne Profile Kupującego', faq: 'Często Zadawane Pytania', competitiveComparison: 'Porównanie z Konkurencją', limitationsToConsider: 'Ograniczenia do Rozważenia', specifications: 'Specyfikacje', keyFeaturesInline: 'Kluczowe cechy', note: 'Uwaga', showAllSpecifications: 'Pokaż wszystkie specyfikacje', showFewerSpecifications: 'Pokaż mniej specyfikacji' },
  el: { productSummary: 'Περίληψη Προϊόντος', keyFeatures: 'Βασικά Χαρακτηριστικά', useCases: 'Περιπτώσεις Χρήσης', bestFor: 'Ιδανικό Για', benefits: 'Οφέλη', idealBuyerProfiles: 'Ιδανικά Προφίλ Αγοραστών', faq: 'Συχνές Ερωτήσεις', competitiveComparison: 'Συγκριτική Ανάλυση', limitationsToConsider: 'Περιορισμοί προς Εξέταση', specifications: 'Προδιαγραφές', keyFeaturesInline: 'Βασικά χαρακτηριστικά', note: 'Σημείωση', showAllSpecifications: 'Εμφάνιση όλων των προδιαγραφών', showFewerSpecifications: 'Εμφάνιση λιγότερων προδιαγραφών' },
  ru: { productSummary: 'Обзор продукта', keyFeatures: 'Ключевые особенности', useCases: 'Сценарии использования', bestFor: 'Лучше всего подходит для', benefits: 'Преимущества', idealBuyerProfiles: 'Идеальные профили покупателей', faq: 'Часто задаваемые вопросы', competitiveComparison: 'Сравнение с конкурентами', limitationsToConsider: 'Ограничения для рассмотрения', specifications: 'Технические характеристики', keyFeaturesInline: 'Ключевые особенности', note: 'Примечание', showAllSpecifications: 'Показать все характеристики', showFewerSpecifications: 'Показать меньше характеристик' },
  ja: { productSummary: '製品概要', keyFeatures: '主な機能', useCases: '使用例', bestFor: '最適な用途', benefits: 'メリット', idealBuyerProfiles: '理想的な購入者プロフィール', faq: 'よくある質問', competitiveComparison: '競合比較', limitationsToConsider: '考慮すべき制限', specifications: '仕様', keyFeaturesInline: '主な機能', note: '注意', showAllSpecifications: 'すべての仕様を表示', showFewerSpecifications: '仕様の表示を減らす' },
  ko: { productSummary: '제품 요약', keyFeatures: '주요 기능', useCases: '사용 사례', bestFor: '최적 사용처', benefits: '장점', idealBuyerProfiles: '이상적인 구매자 프로필', faq: '자주 묻는 질문', competitiveComparison: '경쟁 제품 비교', limitationsToConsider: '고려할 제한 사항', specifications: '사양', keyFeaturesInline: '주요 기능', note: '참고', showAllSpecifications: '모든 사양 보기', showFewerSpecifications: '사양 간략히 보기' },
  zh: { productSummary: '产品摘要', keyFeatures: '主要特性', useCases: '使用场景', bestFor: '最适合', benefits: '优势', idealBuyerProfiles: '理想买家档案', faq: '常见问题', competitiveComparison: '竞品对比', limitationsToConsider: '需考虑的局限', specifications: '规格参数', keyFeaturesInline: '主要特性', note: '注意', showAllSpecifications: '显示全部规格', showFewerSpecifications: '显示较少规格' },
};

const uiLabel = (key: UILabelKey, lang: string | undefined): string =>
  (UI_LABELS[lang || 'en'] || UI_LABELS.en)[key];

interface AvailableLanguage {
  language: string;
  isSource: boolean;
  available: boolean;
}

function SectionNotTranslatedPlaceholder({
  sectionKey,
  languageName,
}: {
  sectionKey: string;
  languageName: string;
}) {
  const label = SECTION_LABELS[sectionKey] || sectionKey;
  return (
    <Card className="border-dashed">
      <CardContent className="py-3 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{label}</span> — not available in {languageName}.
      </CardContent>
    </Card>
  );
}

interface ProductMetadata {
  id?: string;
  name?: string;
  title?: string;
  mpn?: string;
  gtin?: string;
  brand?: string;
  price?: string | number;
  category?: string;
  currency?: string;
  image_url?: string;
  product_id?: string;
  product_url?: string;
  availability?: string;
  additional_data?: Record<string, unknown>;
  [key: string]: unknown;
}

interface LimitationRow {
  limitation: string;
  impact?: string;
  mitigation?: string;
  // The batch prompt also asks for who_affected. It is persisted and no consumer reads it —
  // declared here so the next person sees that it exists rather than rediscovering it.
  who_affected?: string;
}

// Support both camelCase (DB format) and snake_case (transformed format)
interface AEOSections {
  // Top-level fields
  h1?: string;
  title?: string;
  metaDescription?: string;
  featuredSnippet?: string;
  fullDescription?: string;
  keywords?: string[];

  // Summary section (transformed format)
  summary?: {
    featured_snippet?: string;
    full_description?: string;
    key_differentiators?: string[];
  };

  // Best For / Use Cases
  best_for?: Array<string | { badge_text: string; reason?: string; evidence?: string }>;
  useCases?: Array<{ scenario?: string; description?: string; evidence?: string }>;

  // Benefits / Features
  benefits?: Array<{
    benefit_layer?: string;
    benefit_name?: string;
    headline?: string;
    explanation?: string;
    evidence?: string[];
  }>;
  features?: Array<{ title: string; description?: string }>;

  // FAQ
  faq?: Array<{ question: string; answer: string }>;
  faqs?: Array<{ question: string; answer: string }>;

  // Comparisons
  comparison?: {
    comparison_intro?: string;
    where_it_excels?: string[];
    where_to_consider_alternatives?: string[];
    overall_verdict?: string;
    ideal_buyer?: string;
  };
  comparisons?: {
    analysis?: {
      comparison_intro?: string;
      where_it_excels?: string[];
      where_to_consider_alternatives?: string[];
      overall_verdict?: string;
      ideal_buyer?: string;
    };
    brandTiers?: BrandTiers;
    competitorAlternatives?: CompetitorAlternatives;
  };

  // Brand tiers (transformed format)
  comparison_products?: {
    brand_tiers?: BrandTiers;
    competitor_alternatives?: CompetitorAlternatives;
  };

  // Recommendations
  recommendations?: Array<{
    scenario?: string;
    product_name?: string;
    price?: string;
    reason?: string;
  } | string>;

  // Buyer Profiles
  buyerProfiles?: {
    profiles?: Array<{
      profile_name: string;
      description: string;
      fit_score?: number;
      key_priorities?: string[];
      why_this_product?: string;
      potential_concerns?: string | null;
    }>;
    not_ideal_for?: Array<{
      profile: string;
      reason: string;
      better_alternative?: string;
    }>;
  };

  // Limitations — TWO PERSISTED SHAPES, see getLimitations() for why. The type must admit
  // both or it silently endorses the 6% one: this data arrives untyped from the API, so
  // TypeScript never contradicts a declaration that is too narrow, it just agrees with it.
  limitations?: {
    // direct/realtime builder
    assessment?: string;
    items?: LimitationRow[];
    // batch builder
    overall_assessment?: string;
    limitations?: LimitationRow[];
  } | LimitationRow[];

  // Specifications (can be object or array)
  specifications?: Record<string, unknown> | Array<{ display_name?: string; attribute?: string; value: unknown; unit?: string }>;

  // Raw sections (if present)
  rawSections?: Record<string, unknown>;
}

interface BrandTiers {
  brand?: string;
  product_line?: string;
  current_product_tier?: string;
  stats?: {
    products_considered?: number;
    products_with_enrichment?: number;
    avg_similarity?: number;
    min_similarity?: number;
    max_similarity?: number;
  };
  tiers?: {
    good?: TierProduct[];
    better?: TierProduct[];
    best?: TierProduct[];
  };
  attribute_comparison?: AttributeComparison;
}

interface TierProduct {
  id: string;
  name: string;
  tier?: string;
  brand?: string;
  price?: number;
  currency?: string;
  image_url?: string;
  product_url?: string;
  is_current?: boolean;
  similarity?: number;
  has_enrichment?: boolean;
}

interface CompetitorAlternatives {
  products?: TierProduct[];
  price_range?: { min?: number; max?: number };
  stats?: {
    total_in_category?: number;
    products_in_price_range?: number;
    products_with_enrichment?: number;
    avg_similarity?: number;
    min_similarity?: number;
    max_similarity?: number;
  };
  attribute_comparison?: AttributeComparison;
}

interface AttributeComparison {
  products?: Array<{ id: string; name: string; price?: number; product_url?: string; is_current?: boolean }>;
  rows?: Array<{
    attribute: string;
    display_name?: string;
    winner?: string;
    values: Array<{ value: string; unit?: string; product_id?: string; is_current?: boolean }>;
  }>;
}

interface GenerationMetadata {
  generation_model?: string;
  generation_cost_usd?: number;
  input_tokens?: number;
  output_tokens?: number;
  call_count?: number;
}

interface ProductAEOViewerProps {
  content: string | AEOSections;
  metadata?: ProductMetadata;
  title?: string;
  generatedAt?: string;
  generationMetadata?: GenerationMetadata;
  isSuperAdmin?: boolean;
  showCopyButtons?: boolean;
  onCopySection?: (title: string, content: string) => void;
  // Language selector (optional — only the content-review path passes these
  // for now; offering modal can adopt incrementally without breaking).
  currentLanguage?: string;
  sourceLanguage?: string;
  availableLanguages?: AvailableLanguage[];
  expectedSectionKeys?: string[];
  onLanguageChange?: (language: string) => void;
}

// Safely render a value that might be a string or an unexpected object from LLM output
function safeText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // LLM sometimes returns objects keyed by persona name instead of a plain string
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.map(([key, val]) => `${key}: ${typeof val === 'string' ? val : JSON.stringify(val)}`).join('. ');
  }
  return String(value);
}

export function ProductAEOViewer({
  content,
  metadata,
  title,
  generatedAt,
  generationMetadata,
  isSuperAdmin = false,
  showCopyButtons = false,
  onCopySection,
  currentLanguage,
  sourceLanguage,
  availableLanguages,
  expectedSectionKeys,
  onLanguageChange,
}: ProductAEOViewerProps) {
  // Legacy callers without language metadata display source English.
  const attributeLanguage = currentLanguage ?? sourceLanguage ?? 'en';
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  // These are review annotations, not part of the generated customer copy.
  const [showReviewDetails, setShowReviewDetails] = useState(false);
  const [showAllSpecifications, setShowAllSpecifications] = useState(false);
  const specificationsId = useId();
  useEffect(() => { setShowAllSpecifications(false); }, [content, currentLanguage]);

  const handleCopy = (sectionTitle: string, sectionContent: string) => {
    navigator.clipboard.writeText(sectionContent);
    setCopiedSection(sectionTitle);
    setTimeout(() => setCopiedSection(null), 2000);
    onCopySection?.(sectionTitle, sectionContent);
  };

  // Helper to format section content for copying
  const formatSectionContent = (sectionName: string, data: unknown): string => {
    if (!data) return '';
    if (typeof data === 'string') return `## ${sectionName}\n\n${data}`;
    if (Array.isArray(data)) {
      const items = data.map((item, i) => {
        if (typeof item === 'string') return `- ${item}`;
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          // Handle FAQ format
          if (obj.question && obj.answer) {
            return `Q: ${obj.question}\nA: ${obj.answer}`;
          }
          // Handle features/benefits format
          if (obj.title || obj.headline || obj.benefit_name) {
            const title = obj.title || obj.headline || obj.benefit_name;
            const desc = obj.description || obj.explanation || '';
            return `- **${title}**: ${desc}`;
          }
          // Handle use cases
          if (obj.scenario || obj.use_case) {
            const scenario = obj.scenario || obj.use_case;
            const desc = obj.description || obj.why_suitable || '';
            return `- **${scenario}**: ${desc}`;
          }
          return `- ${JSON.stringify(obj)}`;
        }
        return `- ${String(item)}`;
      });
      return `## ${sectionName}\n\n${items.join('\n')}`;
    }
    return `## ${sectionName}\n\n${JSON.stringify(data, null, 2)}`;
  };

  // Parse content if it's a string
  let aeoContent: AEOSections = {};
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      // Handle nested structure where sections are inside a 'sections' key
      aeoContent = parsed.sections || parsed;
    } catch {
      // If it's not JSON, it might be plain text - show as description
      aeoContent = { summary: { full_description: content } };
    }
  } else if (content && typeof content === 'object') {
    // Handle nested structure where sections are inside a 'sections' key
    const contentObj = content as Record<string, unknown>;
    aeoContent = (contentObj.sections as AEOSections) || content;
  }

  const meta = metadata || {};
  const productName = title || meta.name || meta.title || aeoContent.h1 || aeoContent.title || 'Product';

  // Support both old format and new format with snake_case/camelCase variations
  const rawContent = aeoContent as Record<string, unknown>;

  // Normalize sections - handle old format, new format (camelCase), and transformed format (snake_case)
  // U-44: key_differentiators may be promoted to the TOP level (aeoContent.key_differentiators,
  // possibly wrapped { items:[...] }) instead of sitting under summary. Lift + normalize it so the
  // viewer's Key Differentiators section renders for already-promoted payloads too (codex #4073).
  const normalizeKeyDiffs = (v: unknown): string[] | undefined => {
    const arr = Array.isArray(v)
      ? v
      : (v && typeof v === 'object' && Array.isArray((v as { items?: unknown }).items) ? (v as { items: unknown[] }).items : null);
    if (!arr) return undefined;
    const out = arr
      .map(d => (typeof d === 'string'
        ? d
        : (d && typeof d === 'object' ? ((d as Record<string, string>).text || (d as Record<string, string>).point || (d as Record<string, string>).differentiator) : '') || ''))
      .filter(Boolean) as string[];
    return out.length ? out : undefined;
  };

  const getSummary = () => {
    const topLevelKeyDiffs = normalizeKeyDiffs((aeoContent as { key_differentiators?: unknown }).key_differentiators);
    // New format
    if (aeoContent.summary) {
      const s = aeoContent.summary as { key_differentiators?: unknown; [k: string]: unknown };
      return { ...s, key_differentiators: topLevelKeyDiffs ?? normalizeKeyDiffs(s.key_differentiators) };
    }
    // Old format uses 'overview' which can be a string or object with 'description' field
    const overview = rawContent.overview;
    if (overview) {
      const description = typeof overview === 'string' ? overview : (overview as { description?: string }).description;
      return {
        featured_snippet: undefined,
        full_description: description,
        key_differentiators: topLevelKeyDiffs
      };
    }
    return {
      featured_snippet: aeoContent.featuredSnippet,
      full_description: aeoContent.fullDescription,
      key_differentiators: topLevelKeyDiffs
    };
  };

  const getComparison = () => {
    const c = aeoContent.comparison ?? aeoContent.comparisons?.analysis;
    if (!c) return c;
    // ⛔ DROP BLANK BULLETS. Same shape as the blank-answer drop in `getFaqs()` below, and the
    // same reason: the numeric-grounding guard wrote "" into these arrays whenever it stripped
    // every sentence of an entry. Measured on db2 2026-09-03 (7-day window): 112 product pages
    // carry such a blank, and ALL 112 have a matching `numeric_validation.removed[]` entry for
    // that exact field — 112 of 112, none arising any other way. Two were confirmed against the
    // archived model response for the SAME generation attempt: the model returned complete
    // bullets, so the blank is ours.
    // ⚠️ Fixed at source in AEOProductPageService's array walker, but those 112 rows are ALREADY
    // STORED and a generation-time fix cannot heal them — without this they keep rendering a
    // bullet icon with no text next to it.
    // Filtering HERE rather than at the two `.map()` sites covers the copy-to-clipboard builder
    // too, which reads the same arrays and would otherwise emit a bare "- " line.
    // ⛔⛔ A SCALAR STRING MUST BE NORMALISED, NOT RETURNED UNCHANGED — codex-reviewer-2, round 3,
    // BLOCKING. The previous version passed a scalar straight through; `.length > 0` is true for a
    // non-empty string and the later `.map()` then THROWS, breaking the review screen for exactly
    // the rows this PR exists to repair. 209 stored product pages carry the scalar shape (db2), so
    // this is live data, not a hypothetical.
    // ⚠️ Same defect the hosting helper had one round earlier. I fixed it there and did not carry
    // the check across to the twin surface — which is the divergence class this whole PR is about,
    // committed inside the fix for it.
    const dropBlank = (v?: string[] | string): string[] | undefined => {
      if (typeof v === 'string') return v.trim() === '' ? [] : [v];
      return Array.isArray(v) ? v.filter((i) => typeof i === 'string' && i.trim() !== '') : v;
    };
    return {
      ...c,
      where_it_excels: dropBlank(c.where_it_excels),
      where_to_consider_alternatives: dropBlank(c.where_to_consider_alternatives),
    };
  };

  const getBrandTiers = (): BrandTiers | undefined => {
    return aeoContent.comparison_products?.brand_tiers || aeoContent.comparisons?.brandTiers;
  };

  const getCompetitorAlternatives = (): CompetitorAlternatives | undefined => {
    return aeoContent.comparison_products?.competitor_alternatives || aeoContent.comparisons?.competitorAlternatives;
  };

  const getFaqs = () => {
    // Standard format: aeo_content.faqs (array)
    // Also support legacy aeo_content.faq for backward compatibility
    const all = aeoContent.faqs || aeoContent.faq || [];
    // ⛔ DROP ENTRIES WITH A BLANK ANSWER. Measured on db2 2026-08-04 across all published pages:
    // 835 FAQ entries carry an empty answer, on 811 distinct pages — and 471 of those pair the
    // blank with a VISIBLE QUESTION, i.e. we ask something and render nothing under it. No
    // consumer guarded this: hosting (:1345, :2110) and this component all mapped straight over
    // the array.
    //
    // ⚠️ ALL 835 ARE EMPTY STRINGS — zero missing keys, zero JSON nulls. A `!= null` or a
    // key-presence check catches NONE of them; it has to be a trimmed-emptiness test.
    //
    // A question with no answer is strictly worse than no question, so the PAIR is dropped rather
    // than the answer blanked. Read-time, so it repairs all 811 already-published pages at deploy
    // with no regeneration.
    // ⚠️ THIS HIDES THE SYMPTOM, NOT THE CAUSE. Something produced an empty answer and something
    // accepted it; that is a generation-path defect and is filed separately. Do not read this
    // filter as having fixed it.
    return (Array.isArray(all) ? all : []).filter(
      (f: { question?: string; answer?: string }) => typeof f?.answer === 'string' && f.answer.trim() !== ''
    );
  };

  // Handle use cases - multiple formats supported
  const getUseCases = () => {
    // Standard camelCase format
    if (aeoContent.useCases && aeoContent.useCases.length > 0) return aeoContent.useCases;

    // Snake_case format from new generator
    const useCasesSnake = rawContent.use_cases as Array<{
      use_case?: string;
      scenario?: string;
      description?: string;
      why_suitable?: string;
      fit_score?: number;
      fit_rating?: string;
      key_features?: string[];
      limitations?: string | null;
      evidence?: string;
    }> | undefined;

    if (useCasesSnake && useCasesSnake.length > 0) {
      // Normalize to expected format
      return useCasesSnake.map(item => ({
        scenario: item.use_case || item.scenario,
        description: item.why_suitable || item.description,
        evidence: item.key_features?.join(', ') || item.evidence,
        fit_score: item.fit_score,
        fit_rating: item.fit_rating,
        limitations: item.limitations
      }));
    }

    // Old format: bestFor is an array of strings, convert to use case format
    const bestFor = rawContent.bestFor as string[] | undefined;
    if (bestFor && Array.isArray(bestFor)) {
      return bestFor.map(item => ({
        scenario: item,
        description: item,
        evidence: undefined
      }));
    }
    return [];
  };

  // Handle features - new format: features/benefits, old format: keyFeatures
  const getFeatures = () => {
    if (aeoContent.features && aeoContent.features.length > 0) return aeoContent.features;
    // Old format: keyFeatures
    const keyFeatures = rawContent.keyFeatures as Array<{ title: string; description?: string }> | undefined;
    if (keyFeatures) return keyFeatures;
    return [];
  };

  const getRecommendations = () => aeoContent.recommendations || [];
  const getBuyerProfiles = () => aeoContent.buyerProfiles || rawContent.buyer_profiles as typeof aeoContent.buyerProfiles;
  // ⛔ sections.limitations IS PERSISTED IN TWO SHAPES, decided by which builder ran:
  //     DIRECT / realtime  _generateLimitations      -> { items, assessment }
  //     BATCH              _postProcessSection       -> { limitations, overall_assessment }
  //   (AEOProductPageService.js:17727 and :20306; the batch result is assigned VERBATIM to
  //   sections.limitations at :19625, so nothing remaps it before storage.)
  //
  //   Measured on db2 2026-08-04, over every apg row carrying a limitations section — the two
  //   are mutually exclusive, no row has both, none has neither:
  //     { limitations, ... }  BATCH   85,935 rows  (94%, of which 47,622 PUBLISHED)
  //     { items, ... }        DIRECT   5,374 rows  ( 6%, of which  3,279 PUBLISHED)
  //
  // ⛔⛔ THIS GETTER USED TO RETURN THE RAW OBJECT AND THE RENDER GUARDED ON `.items`, SO THE
  //   LIMITATIONS CARD NEVER APPEARED FOR 94% OF THE ESTATE. That is not a display bug: this
  //   is the review surface, so reviewers were pressing Approve without ever seeing the
  //   section. ShopifyWritebackEnqueueService:336 and AeoPageHostingService:2190 already
  //   normalise both shapes and say so in comments — this file and the feed export were the
  //   two consumers that never got the branch (task-qf5ke9ypgh).
  //
  //   The bare-array shape is handled because the other two consumers handle it; the db2
  //   census found ZERO of them, so do not read its presence here as evidence it occurs.
  //   ⚠️ The Array.isArray guard on the RESULT is not belt-and-braces. This content is
  //   LLM-shaped, so a truthy non-array (a string, say) is reachable — and without the guard it
  //   would satisfy the card's `.items.length > 0` check and then throw on `.map`, crashing the
  //   review UI rather than degrading. AeoPageHostingService:2196 guards the same boundary for
  //   the same reason and says so.
  const getLimitations = () => {
    const raw = aeoContent.limitations;
    if (!raw) return undefined;
    if (Array.isArray(raw)) return { items: raw, assessment: '' };
    const candidate = raw.items || raw.limitations;
    return {
      items: Array.isArray(candidate) ? candidate : [],
      assessment: raw.assessment || raw.overall_assessment || '',
    };
  };

  const getSpecifications = () => {
    const specs = aeoContent.specifications;
    if (!specs) return [];
    // If it's an object (key-value pairs), convert to array
    if (!Array.isArray(specs)) {
      return Object.entries(specs).map(([key, value]) => ({
        display_name: key,
        value
      }));
    }
    return specs;
  };

  const summary = getSummary();
  const comparison = getComparison();
  const brandTiers = getBrandTiers();
  const competitorAlternatives = getCompetitorAlternatives();
  const faqs = getFaqs();
  const useCases = getUseCases();
  const features = getFeatures();
  const recommendations = getRecommendations();
  const buyerProfiles = getBuyerProfiles();
  const limitations = getLimitations();
  const specifications = getSpecifications();

  // Format all content for "Copy All" functionality
  const formatAllContentForCopy = (): string => {
    const sections: string[] = [`# ${productName}\n`];

    if (summary?.featured_snippet || summary?.full_description) {
      // summary is snippet-only for publishing now — include the featured_snippet, not just the
      // full_description (codex #4073).
      const parts: string[] = [];
      if (summary.featured_snippet) parts.push(`**Featured Snippet:** ${summary.featured_snippet}`);
      if (summary.full_description) parts.push(summary.full_description);
      sections.push(`## Product Summary\n\n${parts.join('\n\n')}`);
    }
    if (summary?.key_differentiators?.length) {
      // U-44: Key Differentiators is its own section — include it in Copy All.
      sections.push(`## Key Differentiators\n\n${summary.key_differentiators.map((d: string) => `- ${d}`).join('\n')}`);
    }
    if (features.length > 0) {
      sections.push(formatSectionContent('Key Features', features));
    }
    if (useCases.length > 0) {
      sections.push(formatSectionContent('Use Cases', useCases));
    }
    if (aeoContent.benefits?.length) {
      sections.push(formatSectionContent('Benefits', aeoContent.benefits));
    }
    if (faqs.length > 0) {
      sections.push(formatSectionContent('FAQ', faqs));
    }
    if (comparison) {
      let compText = '## Competitive Comparison\n\n';
      if (comparison.comparison_intro) compText += `${comparison.comparison_intro}\n\n`;
      if (comparison.where_it_excels?.length) {
        compText += `**Where it excels:**\n${comparison.where_it_excels.map(e => `- ${e}`).join('\n')}\n\n`;
      }
      if (comparison.where_to_consider_alternatives?.length) {
        compText += `**Consider alternatives if:**\n${comparison.where_to_consider_alternatives.map(e => `- ${e}`).join('\n')}`;
      }
      sections.push(compText);
    }
    if (specifications.length > 0) {
      // The COPIED text is a second surface, and it escapes the screen: reviewers paste it
      // into tickets and mails. Humanising the rendered cell but not this line ships
      // `heel_drop` to the exact place it is hardest to trace back (codex minor @d15e15362).
      const specText = specifications.map(s => `- ${humanizeKey(s.display_name || s.attribute)}: ${formatProductAttributeValue(s.value, s.unit, attributeLanguage)}`).join('\n');
      sections.push(`## Specifications\n\n${specText}`);
    }

    return sections.join('\n\n');
  };

  // Check if we have any AEO content to display (including old format fields)
  const hasAEOContent = summary?.featured_snippet || summary?.full_description ||
    // U-44: a payload that carries ONLY key_differentiators must still count as AEO content
    // (normalized summary source, plus the raw top-level / sections shapes) — codex #4073.
    (summary?.key_differentiators?.length ?? 0) > 0 ||
    ((aeoContent as { key_differentiators?: unknown[] })?.key_differentiators?.length ?? 0) > 0 ||
    ((aeoContent as { sections?: { key_differentiators?: unknown[] } })?.sections?.key_differentiators?.length ?? 0) > 0 ||
    aeoContent.best_for?.length || useCases.length || features.length ||
    aeoContent.benefits?.length || faqs.length || comparison ||
    brandTiers || competitorAlternatives || recommendations.length ||
    buyerProfiles || limitations || specifications.length ||
    rawContent.overview || rawContent.bestFor || rawContent.keyFeatures;

  // Language selector context — only active when ReviewPanel passed these props.
  // When viewing a non-source language, render a placeholder for any expected
  // section that's missing from the translation (no source fallback per brief).
  const isTranslatedView = !!(
    availableLanguages &&
    currentLanguage &&
    sourceLanguage &&
    currentLanguage !== sourceLanguage
  );
  const sectionPresent = (key: string): boolean => {
    // U-44: the Key Differentiators card renders from the NORMALIZED getSummary().key_differentiators
    // (top-level aeoContent.key_differentiators OR legacy summary.key_differentiators), not
    // aeoContent.key_differentiators alone. Mirror that source here so the translated-view
    // placeholder only fires when the rendered card is actually empty (codex #4073 addendum MINOR).
    if (key === 'key_differentiators') return (summary?.key_differentiators?.length ?? 0) > 0;
    const v = (aeoContent as Record<string, unknown>)[key];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return Boolean(v);
  };
  const placeholderFor = (key: string) => {
    if (!isTranslatedView) return null;
    if (!expectedSectionKeys?.includes(key)) return null;
    if (sectionPresent(key)) return null;
    return (
      <SectionNotTranslatedPlaceholder
        sectionKey={key}
        languageName={languageLabel(currentLanguage as string)}
      />
    );
  };

  if (!hasAEOContent && !meta.product_url) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No AEO content available for this product</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="pb-2 border-b">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-500" />
            <span className="text-sm font-medium text-muted-foreground">AI-Enhanced Product Page (AEO)</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={showReviewDetails}
            onClick={() => setShowReviewDetails(value => !value)}
          >
            Review details
          </Button>
          {showCopyButtons && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const allContent = formatAllContentForCopy();
                handleCopy('All Content', allContent);
              }}
            >
              {copiedSection === 'All Content' ? (
                <CheckCircle className="h-4 w-4 text-green-600 mr-1" />
              ) : (
                <Copy className="h-4 w-4 mr-1" />
              )}
              Copy All
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {meta.image_url && (
            <img
              src={meta.image_url}
              alt=""
              className="w-9 h-9 object-contain rounded shrink-0"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          {meta.product_url ? (
            <h3 className="text-lg font-semibold">
              <a
                href={cleanProductUrl(meta.product_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-primary inline-flex items-center gap-1"
              >
                {productName}
                <ExternalLink className="h-4 w-4" />
              </a>
            </h3>
          ) : (
            <h3 className="text-lg font-semibold">{productName}</h3>
          )}
          {availableLanguages && availableLanguages.length > 0 && currentLanguage && (
            <Select
              value={currentLanguage}
              onValueChange={(v) => onLanguageChange?.(v)}
            >
              <SelectTrigger className="ml-2 h-8 w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableLanguages.map((entry) => (
                  <SelectItem
                    key={entry.language}
                    value={entry.language}
                    disabled={!entry.available}
                  >
                    {languageLabel(entry.language)}{entry.isSource ? ' (source)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {generatedAt && (
          <p className="text-xs text-muted-foreground">
            Generated: {new Date(generatedAt).toLocaleString()}
            {isSuperAdmin && generationMetadata?.generation_model && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">
                • Model: {generationMetadata.generation_model}
              </span>
            )}
            {isSuperAdmin && generationMetadata?.generation_cost_usd !== undefined && (
              <span className="ml-2 text-green-600 dark:text-green-400">
                • Cost: ${generationMetadata.generation_cost_usd.toFixed(4)}
              </span>
            )}
          </p>
        )}
      </div>

      {/* Product Summary Section */}
      {placeholderFor('summary')}
      {(summary?.featured_snippet || summary?.full_description) && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {uiLabel('productSummary', currentLanguage)}
              </CardTitle>
              {showCopyButtons && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy('Product Summary', summary?.full_description || summary?.featured_snippet || '')}
                >
                  {copiedSection === 'Product Summary' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Featured Snippet */}
            {summary.featured_snippet && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">Featured Snippet</p>
                <p className="text-sm">{summary.featured_snippet}</p>
              </div>
            )}
            {/* Full Description */}
            {summary.full_description && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {summary.full_description}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Differentiators Section — U-44: its own section, no longer bundled under Summary,
          so the admin viewer mirrors the 14-section preview/writeback/benchmark contract. */}
      {placeholderFor('key_differentiators')}
      {summary?.key_differentiators && summary.key_differentiators.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-amber-500" />
                Key Differentiators
              </CardTitle>
              {showCopyButtons && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy('Key Differentiators', (summary.key_differentiators || []).join('\n'))}
                >
                  {copiedSection === 'Key Differentiators' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {summary.key_differentiators.map((diff: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{diff}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Features Section */}
      {features.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                {uiLabel('keyFeatures', currentLanguage)}
              </CardTitle>
              {showCopyButtons && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy('Key Features', features.map(f => `- ${f.title}: ${f.description || ''}`).join('\n'))}
                >
                  {copiedSection === 'Key Features' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {features.map((feature, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{feature.title}</span>
                    {feature.description && (
                      <p className="text-muted-foreground mt-0.5">{feature.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Use Cases Section */}
      {placeholderFor('use_cases')}
      {useCases.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-4 w-4 text-green-500" />
                {uiLabel('useCases', currentLanguage)}
              </CardTitle>
              {showCopyButtons && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy('Use Cases', useCases.map(uc => `- ${uc.scenario || ''}: ${uc.description || ''}`).join('\n'))}
                >
                  {copiedSection === 'Use Cases' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {useCases.map((useCase: { scenario?: string; description?: string; evidence?: string; fit_score?: number; fit_rating?: string; limitations?: string | null }, index: number) => (
                <div key={index} className="border-b last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center gap-2 mb-1">
                    {useCase.scenario && (
                      <span className="font-medium text-sm">{useCase.scenario}</span>
                    )}
                    {showReviewDetails && useCase.fit_score !== undefined && (
                      <Badge variant={useCase.fit_rating === 'excellent' ? 'default' : 'secondary'} className="text-xs">
                        {Math.round(useCase.fit_score * 100)}% fit
                      </Badge>
                    )}
                  </div>
                  {useCase.description && (
                    <p className="text-sm text-muted-foreground">{useCase.description}</p>
                  )}
                  {showReviewDetails && useCase.evidence && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="font-medium">{uiLabel('keyFeaturesInline', currentLanguage)}:</span> {useCase.evidence}
                    </p>
                  )}
                  {useCase.limitations && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      <span className="font-medium">{uiLabel('note', currentLanguage)}:</span> {useCase.limitations}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Best For Badges */}
      {placeholderFor('best_for')}
      {aeoContent.best_for && aeoContent.best_for.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4" />
              {uiLabel('bestFor', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {aeoContent.best_for.map((item, index: number) => (
                <div key={index} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-sm">
                      {typeof item === 'string' ? item : item.badge_text}
                    </Badge>
                  </div>
                  {typeof item === 'object' && item.reason && (
                    <p className="text-sm text-muted-foreground ml-1">{item.reason}</p>
                  )}
                  {/* codex #5503 r7: raw aeo_content can carry ARRAY evidence — React
                      concatenates arrays with no separators, so join for display
                      (the publish contract serializes to one scalar the same way). */}
                  {showReviewDetails && typeof item === 'object' && item.evidence && (
                    <p className="text-xs text-muted-foreground ml-1">
                      Evidence: {Array.isArray(item.evidence) ? item.evidence.join(', ') : item.evidence}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Benefits */}
      {placeholderFor('benefits')}
      {aeoContent.benefits && Array.isArray(aeoContent.benefits) && aeoContent.benefits.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {uiLabel('benefits', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {aeoContent.benefits.map((benefit, index: number) => (
                <div key={index} className="border-b last:border-0 pb-3 last:pb-0">
                  <div className="flex items-start gap-2 mb-1">
                    {showReviewDetails && benefit.benefit_layer && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {safeText(benefit.benefit_layer)}
                      </Badge>
                    )}
                    <h4 className="font-medium text-sm">{safeText(benefit.headline || benefit.benefit_name)}</h4>
                  </div>
                  {benefit.explanation && (
                    <p className="text-sm text-muted-foreground mb-2">{safeText(benefit.explanation)}</p>
                  )}
                  {showReviewDetails && benefit.evidence && benefit.evidence.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {benefit.evidence.map((ev: string, evIndex: number) => (
                        <Badge key={evIndex} variant="secondary" className="text-xs">
                          {safeText(ev)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Buyer Profiles Section */}
      {placeholderFor('buyer_profiles')}
      {buyerProfiles && buyerProfiles.profiles && buyerProfiles.profiles.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-500" />
              {uiLabel('idealBuyerProfiles', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {buyerProfiles.profiles.map((profile, index: number) => (
                <div key={index} className="border-b last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-sm">{safeText(profile.profile_name)}</h4>
                    {showReviewDetails && profile.fit_score !== undefined && (
                      <Badge variant="outline" className="text-xs">
                        {Math.round(profile.fit_score * 100)}% fit
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{safeText(profile.description)}</p>
                  {profile.key_priorities && profile.key_priorities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {profile.key_priorities.map((priority: string, i: number) => (
                        <Badge key={i} variant="secondary" className="text-xs">{safeText(priority)}</Badge>
                      ))}
                    </div>
                  )}
                  {profile.why_this_product && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      ✓ {safeText(profile.why_this_product)}
                    </p>
                  )}
                  {profile.potential_concerns && profile.potential_concerns !== 'null' && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      ⚠ {safeText(profile.potential_concerns)}
                    </p>
                  )}
                </div>
              ))}

              {/* Not Ideal For */}
              {buyerProfiles.not_ideal_for && buyerProfiles.not_ideal_for.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">Not Ideal For</h4>
                  {buyerProfiles.not_ideal_for.map((item, index: number) => (
                    <div key={index} className="bg-muted/50 p-3 rounded-lg mb-2">
                      <p className="font-medium text-sm">{item.profile}</p>
                      <p className="text-sm text-muted-foreground">{item.reason}</p>
                      {item.better_alternative && (
                        <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                          → {item.better_alternative}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      {placeholderFor('faq')}
      {faqs.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                {uiLabel('faq', currentLanguage)}
              </CardTitle>
              {showCopyButtons && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy('FAQ', faqs.map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n'))}
                >
                  {copiedSection === 'FAQ' ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {faqs.map((faq: { question: string; answer: string }, index: number) => (
                <div key={index} className="border-b last:border-0 pb-4 last:pb-0">
                  <h4 className="font-medium text-sm mb-2">{faq.question}</h4>
                  <p className="text-sm text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Competitive Comparison */}
      {placeholderFor('comparison')}
      {comparison && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {uiLabel('competitiveComparison', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Comparison Intro */}
            {comparison.comparison_intro && (
              <p className="text-sm text-muted-foreground">
                {safeText(comparison.comparison_intro)}
              </p>
            )}
            {/* Where it excels */}
            {comparison.where_it_excels && comparison.where_it_excels.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2 text-green-600 dark:text-green-400">Where it excels</p>
                <ul className="space-y-1">
                  {comparison.where_it_excels.map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{safeText(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Where to consider alternatives */}
            {comparison.where_to_consider_alternatives && comparison.where_to_consider_alternatives.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2 text-amber-600 dark:text-amber-400">Consider alternatives if...</p>
                <ul className="space-y-1">
                  {comparison.where_to_consider_alternatives.map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{safeText(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Overall verdict */}
            {comparison.overall_verdict && (
              <div className="p-3 bg-primary/5 rounded-lg">
                <p className="text-sm font-medium mb-1">Verdict</p>
                <p className="text-sm text-muted-foreground">{safeText(comparison.overall_verdict)}</p>
              </div>
            )}
            {/* Ideal buyer */}
            {comparison.ideal_buyer && (
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Ideal buyer</p>
                  <p className="text-sm text-muted-foreground">{safeText(comparison.ideal_buyer)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Brand Tiers - Good/Better/Best */}
      {placeholderFor('comparison_products')}
      {brandTiers && brandTiers.tiers && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="h-4 w-4 text-blue-500" />
              {brandTiers.brand} Product Range
            </CardTitle>
            {/* ⛔ product_line USED TO BE RENDERED HERE, INSIDE THE TITLE, as "({product_line} series)".
                That made the header assert a SCOPE the rows do not honour: the header said
                "Shimano Product Range (mt900 series)" above SM-RT70 / RT-CL800 / RT-EM810 / SM-RT86,
                four different series. Reported by Thierry; measured 2026-08-04 on db2 as 28,375 of
                the 31,081 published pages that assert a line — 91.3%, i.e. the DEFAULT outcome.

                THE ROWS WERE NEVER FILTERED TO THE LINE and never could be: they are selected by
                brand + similarity, and constraining them to the line would EMPTY 6,006 tables and
                leave 97.6% with fewer than three rows (6.9 rows/table → 1.33). So the header was
                the wrong half to keep.

                🔑 product_line IS NOT A CLAIM ABOUT THE ROWS. At the write site
                (AEOProductPageService.js:11356-11360) it is a sibling of `brand`,
                `brand_market_tier` and `current_product_tier` — all SUBJECT-PRODUCT metadata — while
                the rows are nested one level down under `tiers`. `brand` genuinely scopes the rows;
                `product_line` never did. Rendering them adjacently is what made the second read as
                scope. So it is MOVED to the subject-product line below, not deleted: the statement
                "this product is in the mt900 series" is TRUE and worth keeping.

                AeoPageHostingService:1439 already renders brand only and has always been correct;
                this gives the admin the branch it never got. */}
            {(brandTiers.current_product_tier || brandTiers.product_line) && (
              <CardDescription>
                This product
                {brandTiers.product_line && <> is in the <span className="font-medium">{brandTiers.product_line}</span> series</>}
                {brandTiers.current_product_tier && (
                  <> {brandTiers.product_line ? 'and sits in the' : 'is in the'} <Badge className={
                    brandTiers.current_product_tier === 'best' ? 'bg-purple-500' :
                    brandTiers.current_product_tier === 'better' ? 'bg-blue-500' : 'bg-green-500'
                  }>{brandTiers.current_product_tier}</Badge> tier</>
                )}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Comparison Stats */}
            {brandTiers.stats && (
              <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg text-xs">
                <Badge variant="outline" className="gap-1">
                  <Package className="h-3 w-3" />
                  {brandTiers.stats.products_considered} products compared
                </Badge>
                {brandTiers.stats.products_with_enrichment !== undefined && (
                  <Badge variant="outline" className={brandTiers.stats.products_with_enrichment > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}>
                    <Sparkles className="h-3 w-3 mr-1" />
                    {brandTiers.stats.products_with_enrichment} AI-enriched
                  </Badge>
                )}
                {brandTiers.stats.avg_similarity !== undefined && brandTiers.stats.avg_similarity > 0 && (
                  <Badge variant="outline" className="gap-1">
                    Similarity: {(brandTiers.stats.min_similarity! * 100).toFixed(0)}-{(brandTiers.stats.max_similarity! * 100).toFixed(0)}% (avg {(brandTiers.stats.avg_similarity * 100).toFixed(0)}%)
                  </Badge>
                )}
              </div>
            )}

            {/* Tier Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Good Tier */}
              <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-green-500">Good</Badge>
                  <span className="text-xs text-muted-foreground">Entry-level</span>
                </div>
                {brandTiers.tiers.good?.map((prod) => (
                  <div key={prod.id} className={`p-2 rounded mb-2 ${prod.is_current ? 'bg-primary/10 border-2 border-primary' : 'bg-background'}`}>
                    <p className="text-sm font-medium line-clamp-1">{prod.name}</p>
                    {prod.price != null && <p className="text-sm font-semibold text-green-600">€{prod.price.toFixed(2)}</p>}
                    <div className="flex gap-1 flex-wrap mt-1">
                      {prod.is_current && <Badge variant="outline" className="text-xs">Current</Badge>}
                      {!prod.is_current && prod.similarity !== undefined && prod.similarity > 0 && (
                        <Badge variant="secondary" className="text-xs">{(prod.similarity * 100).toFixed(0)}%</Badge>
                      )}
                      {prod.has_enrichment && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">AI</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {(!brandTiers.tiers.good || brandTiers.tiers.good.length === 0) && (
                  <p className="text-xs text-muted-foreground">No products in this tier</p>
                )}
              </div>

              {/* Better Tier */}
              <div className="border rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-blue-500">Better</Badge>
                  <span className="text-xs text-muted-foreground">Mid-range</span>
                </div>
                {brandTiers.tiers.better?.map((prod) => (
                  <div key={prod.id} className={`p-2 rounded mb-2 ${prod.is_current ? 'bg-primary/10 border-2 border-primary' : 'bg-background'}`}>
                    <p className="text-sm font-medium line-clamp-1">{prod.name}</p>
                    {prod.price != null && <p className="text-sm font-semibold text-blue-600">€{prod.price.toFixed(2)}</p>}
                    <div className="flex gap-1 flex-wrap mt-1">
                      {prod.is_current && <Badge variant="outline" className="text-xs">Current</Badge>}
                      {!prod.is_current && prod.similarity !== undefined && prod.similarity > 0 && (
                        <Badge variant="secondary" className="text-xs">{(prod.similarity * 100).toFixed(0)}%</Badge>
                      )}
                      {prod.has_enrichment && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">AI</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {(!brandTiers.tiers.better || brandTiers.tiers.better.length === 0) && (
                  <p className="text-xs text-muted-foreground">No products in this tier</p>
                )}
              </div>

              {/* Best Tier */}
              <div className="border rounded-lg p-3 bg-purple-50 dark:bg-purple-900/20">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-purple-500">Best</Badge>
                  <span className="text-xs text-muted-foreground">Premium</span>
                </div>
                {brandTiers.tiers.best?.map((prod) => (
                  <div key={prod.id} className={`p-2 rounded mb-2 ${prod.is_current ? 'bg-primary/10 border-2 border-primary' : 'bg-background'}`}>
                    <p className="text-sm font-medium line-clamp-1">{prod.name}</p>
                    {prod.price != null && <p className="text-sm font-semibold text-purple-600">€{prod.price.toFixed(2)}</p>}
                    <div className="flex gap-1 flex-wrap mt-1">
                      {prod.is_current && <Badge variant="outline" className="text-xs">Current</Badge>}
                      {!prod.is_current && prod.similarity !== undefined && prod.similarity > 0 && (
                        <Badge variant="secondary" className="text-xs">{(prod.similarity * 100).toFixed(0)}%</Badge>
                      )}
                      {prod.has_enrichment && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">AI</Badge>
                      )}
                    </div>
                  </div>
                ))}
                {(!brandTiers.tiers.best || brandTiers.tiers.best.length === 0) && (
                  <p className="text-xs text-muted-foreground">No products in this tier</p>
                )}
              </div>
            </div>

            {/* Brand Attribute Comparison Table */}
            {brandTiers.attribute_comparison && brandTiers.attribute_comparison.rows && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Specs Comparison ({brandTiers.brand})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium">Attribute</th>
                        {brandTiers.attribute_comparison.products?.map((p) => (
                          <th key={p.id} className={`text-left py-2 px-2 font-medium ${p.is_current ? 'bg-primary/10' : ''}`}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="max-w-[120px] cursor-default">
                                    {p.product_url && !p.is_current ? (
                                      <a href={cleanProductUrl(p.product_url)} target="_blank" rel="noopener noreferrer" className="text-xs block">
                                        {p.name.length > 28 ? p.name.slice(0, 28) + '...' : p.name}
                                      </a>
                                    ) : (
                                      <p className="text-xs">{p.name.length > 28 ? p.name.slice(0, 28) + '...' : p.name}</p>
                                    )}
                                    {p.price != null && <p className="text-xs text-muted-foreground">€{p.price.toFixed(0)}</p>}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent><p className="max-w-[200px]">{p.name}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {brandTiers.attribute_comparison.rows.slice(0, 10).map((row) => (
                        <tr key={row.attribute} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-2 font-medium text-xs">{humanizeKey(row.display_name || row.attribute)}</td>
                          {row.values.map((val, idx) => (
                            <td key={idx} className={`py-2 px-2 text-xs ${val.is_current ? 'bg-primary/5' : ''} ${row.winner === val.product_id ? 'font-bold text-green-600' : ''}`}>
                              {formatProductAttributeValue(val.value, val.unit, attributeLanguage)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Competitor Alternatives */}
      {competitorAlternatives && competitorAlternatives.products && competitorAlternatives.products.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-amber-500" />
              Competitor Alternatives
            </CardTitle>
            <CardDescription>
              Similar products from other brands
              {competitorAlternatives.price_range?.min != null && competitorAlternatives.price_range?.max != null &&
                ` (€${competitorAlternatives.price_range.min.toFixed(0)} - €${competitorAlternatives.price_range.max.toFixed(0)})`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Competitor Stats */}
            {competitorAlternatives.stats && (
              <div className="flex flex-wrap gap-2 p-2 bg-muted/50 rounded-lg text-xs">
                <Badge variant="outline" className="gap-1">
                  <Package className="h-3 w-3" />
                  {competitorAlternatives.stats.total_in_category} in category, {competitorAlternatives.stats.products_in_price_range} in price range
                </Badge>
                {competitorAlternatives.stats.products_with_enrichment !== undefined && (
                  <Badge variant="outline" className={competitorAlternatives.stats.products_with_enrichment > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}>
                    <Sparkles className="h-3 w-3 mr-1" />
                    {competitorAlternatives.stats.products_with_enrichment} AI-enriched
                  </Badge>
                )}
                {competitorAlternatives.stats.avg_similarity !== undefined && competitorAlternatives.stats.avg_similarity > 0 && (
                  <Badge variant="outline" className="gap-1">
                    Similarity: {(competitorAlternatives.stats.min_similarity! * 100).toFixed(0)}-{(competitorAlternatives.stats.max_similarity! * 100).toFixed(0)}% (avg {(competitorAlternatives.stats.avg_similarity * 100).toFixed(0)}%)
                  </Badge>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {competitorAlternatives.products.map((prod) => (
                <div key={prod.id} className="border rounded-lg p-3 hover:bg-accent/50 transition-colors">
                  {prod.image_url && (
                    <img src={prod.image_url} alt={prod.name} className="w-full h-16 object-contain mb-2 rounded" />
                  )}
                  <p className="text-sm font-medium line-clamp-2">{prod.name}</p>
                  <div className="flex justify-between items-center mt-1">
                    {prod.price != null && <span className="text-sm font-semibold text-primary">€{prod.price.toFixed(2)}</span>}
                    <Badge variant="outline" className="text-xs">{prod.brand}</Badge>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {prod.similarity !== undefined && prod.similarity > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {(prod.similarity * 100).toFixed(0)}% similar
                      </Badge>
                    )}
                    {prod.has_enrichment && (
                      <Badge variant="outline" className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        <Sparkles className="h-2 w-2 mr-0.5" />
                        AI
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Competitor Attribute Comparison Table */}
            {competitorAlternatives.attribute_comparison && competitorAlternatives.attribute_comparison.rows && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Specs Comparison vs Competitors
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium">Attribute</th>
                        {competitorAlternatives.attribute_comparison.products?.map((p) => (
                          <th key={p.id} className={`text-left py-2 px-2 font-medium ${p.is_current ? 'bg-primary/10' : ''}`}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="max-w-[120px] cursor-default">
                                    {p.product_url && !p.is_current ? (
                                      <a href={cleanProductUrl(p.product_url)} target="_blank" rel="noopener noreferrer" className="text-xs block">
                                        {p.name.length > 28 ? p.name.slice(0, 28) + '...' : p.name}
                                      </a>
                                    ) : (
                                      <p className="text-xs">{p.name.length > 28 ? p.name.slice(0, 28) + '...' : p.name}</p>
                                    )}
                                    {p.price != null && <p className="text-xs text-muted-foreground">€{p.price.toFixed(0)}</p>}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent><p className="max-w-[200px]">{p.name}</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {competitorAlternatives.attribute_comparison.rows.slice(0, 10).map((row) => (
                        <tr key={row.attribute} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-2 font-medium text-xs">{humanizeKey(row.display_name || row.attribute)}</td>
                          {row.values.map((val, idx) => (
                            <td key={idx} className={`py-2 px-2 text-xs ${val.is_current ? 'bg-primary/5' : ''} ${row.winner === val.product_id ? 'font-bold text-green-600' : ''}`}>
                              {formatProductAttributeValue(val.value, val.unit, attributeLanguage)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alternative Recommendations */}
      {placeholderFor('recommendations')}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              Alternative Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recommendations.map((rec, index: number) => (
                <div key={index} className="border-b last:border-0 pb-3 last:pb-0">
                  {typeof rec === 'string' ? (
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">{rec.scenario}</Badge>
                      </div>
                      <p className="text-sm font-medium">{rec.product_name}</p>
                      {rec.price && <p className="text-xs text-muted-foreground">{rec.price}</p>}
                      <p className="text-sm text-muted-foreground mt-1">{rec.reason}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Limitations Section */}
      {placeholderFor('limitations')}
      {limitations && limitations.items && limitations.items.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              {uiLabel('limitationsToConsider', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {limitations.assessment && (
              <p className="text-sm text-muted-foreground mb-4">{limitations.assessment}</p>
            )}
            <div className="space-y-3">
              {limitations.items.map((item, index: number) => (
                <div key={index} className="border-b last:border-0 pb-3 last:pb-0">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">{item.limitation}</p>
                  {item.impact && (
                    <p className="text-sm text-muted-foreground mt-1">
                      <span className="font-medium">Impact:</span> {item.impact}
                    </p>
                  )}
                  {item.mitigation && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      <span className="font-medium">Mitigation:</span> {item.mitigation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Specifications Table */}
      {placeholderFor('specifications')}
      {specifications.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <List className="h-4 w-4" />
              {uiLabel('specifications', currentLanguage)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div id={specificationsId} className="space-y-1">
              {(showAllSpecifications ? specifications : specifications.slice(0, 15)).map((spec, index: number) => (
                <div key={index} className="flex justify-between items-center text-sm py-1 border-b last:border-0">
                  <span className="font-medium">{humanizeKey(spec.display_name || spec.attribute)}</span>
                  <span className="text-muted-foreground">
                    {formatProductAttributeValue(spec.value, spec.unit, attributeLanguage)}
                  </span>
                </div>
              ))}
            </div>
            {specifications.length > 15 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2"
                aria-expanded={showAllSpecifications}
                aria-controls={specificationsId}
                onClick={() => setShowAllSpecifications(value => !value)}
              >
                {uiLabel(showAllSpecifications ? 'showFewerSpecifications' : 'showAllSpecifications', currentLanguage)}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* No catch-all for unknown expected keys: sections like
          smart_alternatives and problem_solver are internal scaffolding
          (recommender context, sector metadata) with no viewer UI. They
          aren't displayed in source either, so flagging them as
          "not translated" would be a phantom gap. */}

      {/* Product Link */}
      {meta.product_url && (
        <a
          href={cleanProductUrl(meta.product_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          View on store
        </a>
      )}
    </div>
  );
}

export default ProductAEOViewer;
